import BigNumber from "bignumber.js";

import { Globalization, INumberFormatInfoOrienation } from "../../Dom/Globalization";
import { IBigNumber } from "./IBigNumber";

export class BigNumberProxy implements IBigNumber {
    private bn: BigNumber;
    private _originalValue: string | number | BigNumber;

    private __length: number | undefined = undefined;
    private get _length(): number {
        if (this.__length) return this.__length;
        let strNum: string = "";

        if (this._originalValue == undefined) {
            return 0;
        } else if (typeof this._originalValue === "string") {
            strNum = this._originalValue;
        } else if (typeof this._originalValue === "number") {
            strNum = this._originalValue.toPrecision();
        } else {
            this.__length = parseInt(this._originalValue.toPrecision(1).replace(/\d[.?\d]*e[+|-](\d+)/, "$1")) + 1;
            return this.__length;
        }

        let lng = strNum.length;
        if (strNum.includes(".")) lng--;
        if (strNum.includes("-")) lng--;

        this.__length = lng;
        return this.__length;
    }

    constructor(value: string | number | BigNumber) {
        this._originalValue = value;
        this.bn = new BigNumber(value);
    }

    public compare(value: any): boolean {
        if (value instanceof BigNumberProxy) {
            return this.toString() == value.toString();
        }
        return false;
    }

    public toString(): string {
        if (this.bn.integerValue().toString() == "0" || this._length == 0) return this.bn.toPrecision();
        return this.bn.toPrecision(this._length);
    }

    public toLocaleString(locales?: string, options?: Intl.NumberFormatOptions): string {
        if (!locales) locales = Globalization.actualCulture;

        // vrátí zadaný počet nul
        const f_getZero = (cnt: number) => {
            let z = "";
            for (let i = 0; i < cnt; i++) {
                z += "0";
            }

            return z;
        };

        // zkontroluje zda předaná čáselná část obsahuje jen nuly
        const f_checkOnlyZero = (num: string) => {
            if (!num) return true;

            for (const c of num) if (c != "0") return false;

            return true;
        };

        const numFormat = Globalization.getNumberFormat(locales);
        const opt = BigNumberProxy._getLocaleOptions(locales, options);
        let bn = new BigNumber(this.bn.toPrecision(this._length));

        let replaceText: string | undefined = undefined;

        // Zpracování standartních nastaveni pro NumberOptions v toLocaleString
        if (opt.style == "percent") {
            bn = bn.multipliedBy(100);
            const percentInfo = Globalization.getPercentFormatInfo(locales);
            replaceText = percentInfo.position;
        }
        if (opt.style == "currency" && opt.currency) {
            const currencyInfo = Globalization.getCurrencyFormatInfo(locales, opt.currency);
            if (opt.currencyDisplay == "symbol") replaceText = currencyInfo.positions.symbol;
            else if (opt.currencyDisplay == "code") replaceText = currencyInfo.positions.code;
            else if (opt.currencyDisplay == "name") replaceText = currencyInfo.positions.name;
        }

        // toPrecision vrátí číslo bez zaokrouhlování tak jak je v invariantCulture
        let numParts = bn.toPrecision(this._length).split(".");

        let intPart = numParts[0]; // celočíselná část
        let decPart = numParts.length == 2 ? numParts[1] : ""; // desetinná část
        const isPositive = intPart[0] != "-";

        if (!isPositive) intPart = intPart.substring(1);

        // kontrola na maximum desetinnych mist
        if (opt.maximumFractionDigits != undefined) {
            if (opt.maximumFractionDigits < decPart.length) {
                // orezanim desetinnych mist muze dojit k zaokrouhleni,
                // a proto vytvoříme znova číslo pomoci knihovyn BigNumber a zaokrouhlíme pomocí toFixed,
                // ktera by měla zaokrouhlení správně vyhodnotit a znova rozdelime na dve casti
                const bn = new BigNumber(intPart + "." + decPart);
                numParts = bn.toFixed(opt.maximumFractionDigits).split(".");
                intPart = numParts[0];
                decPart = numParts[1];
            }
        }

        // kontrola na vyznamna cisla
        if (opt.maximumSignificantDigits != undefined) {
            if (opt.maximumSignificantDigits < decPart.length + intPart.length) {
                const bn = new BigNumber(intPart + "." + decPart);
                const bn_string = bn.precision(opt.maximumSignificantDigits).toString();
                if (opt.maximumSignificantDigits <= intPart.length && intPart != "0") {
                    // tahle cast je tu proto, ze tahle knihovna cislo, ktere "zkracuje" svou celou cast, da do formatu 1.2e6 coz je potreba eliminovat
                    const originalLength = intPart.length;
                    intPart = bn_string.replace(".", "");
                    const eIndex = intPart.indexOf("e");
                    intPart = eIndex >= 0 ? intPart.substring(0, eIndex) : intPart;

                    intPart += f_getZero(originalLength - intPart.length);
                    decPart = "";
                } else {
                    numParts = bn_string.split(".");
                    intPart = numParts[0];
                    decPart = numParts[1];
                }
            }
        }

        if (f_checkOnlyZero(decPart)) decPart = "";

        let beforeDecimalPoint = "";
        let afterDecimalPoint = "";
        let f_appendChar: (ch: string, target: string) => string;

        // zpracovani funkce pro zapis cisel v opacne orientaci znaků
        if (numFormat.orientation == INumberFormatInfoOrienation.leftToRight) {
            f_appendChar = (ch: string, target: string) => {
                return ch + target;
            };
        } else {
            f_appendChar = (ch: string, target: string) => {
                return target + ch;
            };
        }

        for (let i = intPart.length - 1, grpCnt = 0, chCnt = 1; i >= 0; i--) {
            const c = intPart[i];
            beforeDecimalPoint = f_appendChar(c, beforeDecimalPoint);

            // zpracovani groupování čísel podle dané culture
            if (opt.useGrouping) {
                // secondaryGroupSize, nektere kultury maji pro prvni groupu jina pravidla nez pro zbyvajici
                // groupani se vztahuje jen na celociselnou cast
                if ((chCnt == numFormat.secondaryGroupSize && grpCnt == 0 || chCnt == numFormat.groupSize && grpCnt > 0) && i > 0) {
                    beforeDecimalPoint = f_appendChar(numFormat.groupSeparator, beforeDecimalPoint);
                    grpCnt++;
                    chCnt = 1;
                } else chCnt++;
            }
        }

        for (let i = decPart.length - 1; i >= 0; i--) {
            const c = decPart[i];
            afterDecimalPoint = f_appendChar(c, afterDecimalPoint);
        }

        // kontrola na minimum desetinnych cisel
        if (opt.minimumFractionDigits != undefined) {
            if (opt.minimumFractionDigits > 0) {
                if (afterDecimalPoint.length < opt.minimumFractionDigits) {
                    afterDecimalPoint += f_getZero(opt.minimumFractionDigits - afterDecimalPoint.length);
                }
            }
        }

        // při použití jiných než arabských čísel musíme provést jejich nahrazení z tabulky znaků
        if (!numFormat.useArabicNumbers) {
            let i = 0;
            for (const ch of numFormat.numbers) {
                afterDecimalPoint = afterDecimalPoint.replace(new RegExp("" + i, "g"), ch);
                beforeDecimalPoint = beforeDecimalPoint.replace(new RegExp("" + i++, "g"), ch);
            }
        }

        if (afterDecimalPoint.length > 0) {
            beforeDecimalPoint = beforeDecimalPoint + numFormat.decimalSeparator + afterDecimalPoint;
        }

        // nektere kultury maji jinak umisteny minus neb ho zapisuji uplne jinak a proto se provede nahrazeni do sablony
        if (!isPositive) beforeDecimalPoint = numFormat.positions.minus.replace("#cv#", beforeDecimalPoint);

        if (replaceText) return replaceText.replace("#cv#", beforeDecimalPoint);

        return beforeDecimalPoint;
    }

    private static _getLocaleOptions(culture: string, options: Intl.NumberFormatOptions = {}): Intl.NumberFormatOptions {
        const opt: any = {};

        opt.style = options.style == undefined ? "decimal" : options.style.toLowerCase();

        if (opt.style != "decimal" && opt.style != "percent" && opt.style != "currency") {
            throw new RangeError("Value " + opt.style + " out of range for numberformat options property style");
        }

        opt.useGrouping = options.useGrouping == undefined ? true : options.useGrouping;

        if (opt.style == "currency") {
            if (!options.currency) {
                throw new TypeError("Currency code is required with currency style.");
            }
            opt.currency = options.currency;
            opt.currencyDisplay = options.currencyDisplay || "symbol"; // "symbol" [€], "code" [EUR], "name" [Euro]
        }

        if (options.minimumSignificantDigits || options.maximumSignificantDigits) {
            opt.minimumSignificantDigits = options.minimumSignificantDigits || 1;
            opt.maximumSignificantDigits = options.maximumSignificantDigits;

            if (opt.minimumSignificantDigits < 1) {
                throw new RangeError("minimumSignificantDigits value is out of range.");
            }

            if (opt.maximumSignificantDigits != undefined) {
                if (opt.maximumSignificantDigits < 1 || opt.maximumSignificantDigits < opt.minimumSignificantDigits) {
                    throw new RangeError("maximumSignificantDigits value is out of range.");
                }
            }
        } else {
            opt.minimumIntegerDigits = options.minimumIntegerDigits || 1;

            if (!options.minimumFractionDigits) {
                if (opt.style == "currency") {
                    const currencyInfo = Globalization.getCurrencyFormatInfo(culture, opt.currency);
                    opt.minimumFractionDigits = currencyInfo.minorUnit;
                } else opt.minimumFractionDigits = 0;
            } else opt.minimumFractionDigits = options.minimumFractionDigits;

            opt.minimumFractionDigits = options.minimumFractionDigits || (opt.style == "currency" ? 2 : 0);

            if (options.maximumFractionDigits == undefined) {
                if (opt.style == "decimal") {
                    opt.maximumFractionDigits = opt.minimumFractionDigits < 3 ? 3 : opt.minimumFractionDigits;
                } else if (opt.style == "currency") {
                    const currencyInfo = Globalization.getCurrencyFormatInfo(culture, opt.currency);
                    opt.maximumFractionDigits = opt.minimumFractionDigits < currencyInfo.minorUnit ? currencyInfo.minorUnit : opt.minimumFractionDigits;
                } else if (opt.style == "percent") {
                    opt.maximumFractionDigits = opt.minimumFractionDigits > 0 ? opt.minimumFractionDigits : 0;
                }
            } else {
                opt.maximumFractionDigits = options.maximumFractionDigits;
            }
        }

        return opt;
    }

    public plus(value: number): BigNumberProxy {
        return new BigNumberProxy(this.bn.plus(value));
    }

    /** @inheritDoc */
    public gte(n: number | string): boolean {
        return this.bn.gte(n);
    }

    /** @inheritDoc */
    public lte(n: number | string): boolean {
        return this.bn.lte(n);
    }

    /** @inheritDoc */
    public isNaN(): boolean {
        return this.bn.isNaN();
    }
}

(window as any).BigNumber = BigNumberProxy;
